/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */
import * as turf from "@turf/turf";
import debounce from "lodash/debounce";
import mapboxgl from "mapbox-gl";
import aLineIcon from "../../assets/images/a-line-img.svg";
import bLineIcon from "../../assets/images/b-line-img.svg";
import type {
  AbLine,
  NewTrialStateInterface,
} from "../../pages/new-trail/types";
import type { PolygonGeoJSON } from "../components/MapComponent/type";
import {
  checkAlignmentWithCurrentState,
  clearStripLinesCache,
} from "../components/MapComponent/utils";
import { drawMachineryGridPure } from "./abLine";

const FEATURES_INDEX_ZERO = 0;
const INDEX_ZERO = 0;
const INDEX_ONE = 1;
let previousAbLine: AbLine[] | null = null;
const mapboxMarkers = new Map<string, mapboxgl.Marker>();

interface AddABLinesToMapOptions {
  setNewTrialState: any;
  createCustomMarkerElement: (icon: string) => HTMLElement;
  getAbLineColor: (id: number, type: string) => string;
  currentStep?: number;
  trialProtocolData?: any;
}

export const addABLinesToMap = (
  map: mapboxgl.Map | null,
  newTrialState: NewTrialStateInterface,
  polygonGeoJSON: PolygonGeoJSON,
  options: AddABLinesToMapOptions
): void => {
  const {
    setNewTrialState,
    createCustomMarkerElement,
    getAbLineColor,
    currentStep,
    trialProtocolData,
  } = options;
  if (!map || !newTrialState.ab_line) return;

  const polygon = polygonGeoJSON.features[FEATURES_INDEX_ZERO].geometry;

  if (previousAbLine) {
    const previousIds = new Set(
      previousAbLine.map((abItem: AbLine) => abItem.id)
    );
    const currentIds = new Set(
      newTrialState.ab_line.map((abItem: AbLine) => abItem.id)
    );

    previousIds.forEach((id) => {
      if (!currentIds.has(id)) {
        removeLayerAndSources(map, id as number);
      }
    });
  }

  newTrialState.ab_line.forEach((abItem: AbLine, abLineIndex: number) => {
    const sourceId = `ab-line-geojson-data-${abItem.id as number}`;
    const layerId = `ab-line-layer-${abItem.id as number}`;
    if (!map.getSource(sourceId) || !map.getLayer(layerId)) {
      createNewLineLayer(map, abItem, polygon, sourceId, layerId, {
        newTrialState,
        setNewTrialState,
        polygonGeoJSON,
        createCustomMarkerElement,
        getAbLineColor,
        abLineIndex,
        currentStep,
        trialProtocolData,
      });
    } else {
      // Markers are always recreated in addMarkersToAbLine, so just call it
      const [marker1, marker2] = addMarkersToAbLine(
        map,
        abItem.geojson.geometry.coordinates[INDEX_ZERO],
        abItem.geojson.geometry.coordinates[INDEX_ONE],
        abItem,
        {
          createCustomMarkerElement,
          sourceId,
          polygonGeoJSON,
          getAbLineColor,
          setNewTrialState,
          abLineIndex,
          currentStep,
          trialProtocolData,
        }
      );

      updateLayerVisibilityAndSelection(
        map,
        layerId,
        abItem.visible,
        abItem.id as number,
        currentStep === 1,
        polygonGeoJSON
      );

      const machinaryGridArray = getMachineryGridArray(newTrialState);

      updateLineSource({
        machinaryGridArray,
        marker1,
        marker2,
        map,
        abItem,
        polygonGeoJSON,
        getAbLineColor,
        trialProtocolData,
        currentStep,
      });
      // Only update the line source and state on drag, not on every render
      // updateLineSourceOnDrag is now only called from the drag handler in addMarkersToAbLine
    }
  });
  previousAbLine = newTrialState.ab_line.map((abItem) => ({ ...abItem }));
};

interface AddMarkersToAbLineOptions {
  createCustomMarkerElement: (icon: string) => HTMLElement;
  sourceId: string;
  polygonGeoJSON: any;
  getAbLineColor: (id: number, type: string) => string;
  setNewTrialState?: (
    updater: (prevState: NewTrialStateInterface) => NewTrialStateInterface
  ) => void;
  abLineIndex?: number;
  currentStep?: number;
  trialProtocolData?: any;
}

const addMarkersToAbLine = (
  map: mapboxgl.Map,
  pointA: mapboxgl.LngLatLike,
  pointB: mapboxgl.LngLatLike,
  abItem: AbLine,
  options: AddMarkersToAbLineOptions
): [mapboxgl.Marker, mapboxgl.Marker] => {
  const {
    createCustomMarkerElement,
    sourceId,
    polygonGeoJSON,
    getAbLineColor,
    setNewTrialState,
    abLineIndex,
    currentStep,
    trialProtocolData,
  } = options;
  const marker1Id = `marker1-${abItem.id as number}`;
  const marker2Id = `marker2-${abItem.id as number}`;

  // Remove existing markers if they exist
  const existingMarker1 = mapboxMarkers.get(marker1Id);
  if (existingMarker1) {
    existingMarker1.remove();
    mapboxMarkers.delete(marker1Id);
  }
  const existingMarker2 = mapboxMarkers.get(marker2Id);
  if (existingMarker2) {
    existingMarker2.remove();
    mapboxMarkers.delete(marker2Id);
  }

  // Create new markers
  const marker1 = createMarker(
    map,
    pointA,
    aLineIcon,
    INDEX_ZERO,
    abItem.id as number,
    createCustomMarkerElement,
    abItem.selected
  );

  const marker2 = createMarker(
    map,
    pointB,
    bLineIcon,
    INDEX_ONE,
    abItem.id as number,
    createCustomMarkerElement,
    abItem.selected
  );

  const debouncedUpdateLineSourceOnDrag = debounce(() => {
    updateLineSourceOnDrag({
      marker1,
      marker2,
      map,
      sourceId,
      abItem,
      polygonGeoJSON,
      getAbLineColor,
      setNewTrialState,
      abLineIndex,
      trialProtocolData,
      currentStep,
    });
  }, 500);

  marker1.on("drag", debouncedUpdateLineSourceOnDrag);
  marker2.on("drag", debouncedUpdateLineSourceOnDrag);

  return [marker1, marker2];
};

interface CreateNewLineLayerOptions {
  newTrialState: any;
  setNewTrialState: any;
  polygonGeoJSON: any;
  createCustomMarkerElement: (icon: string) => HTMLElement;
  getAbLineColor: (id: number, type: string) => string;
  abLineIndex: number;
  currentStep?: number;
  trialProtocolData?: any;
}

const createNewLineLayer = (
  map: mapboxgl.Map,
  abItem: AbLine,
  polygon: turf.Polygon | turf.MultiPolygon,
  sourceId: string,
  layerId: string,
  options: CreateNewLineLayerOptions
): void => {
  const {
    newTrialState,
    setNewTrialState,
    polygonGeoJSON,
    createCustomMarkerElement,
    getAbLineColor,
    abLineIndex,
    currentStep,
    trialProtocolData,
  } = options;
  const [pointA, pointB] = getLinePoints(abItem, polygon);

  // Persist the initialized line coordinates so popup fields reflect
  // the exact AB line that is visible on the map.
  if (setNewTrialState) {
    setNewTrialState((prevState: NewTrialStateInterface) => {
      const currentLine = prevState.ab_line?.[abLineIndex];
      if (!currentLine) {
        return prevState;
      }

      const currentCoords = currentLine?.geojson?.geometry?.coordinates;
      const nextCoords = [pointA, pointB];
      if (JSON.stringify(currentCoords) === JSON.stringify(nextCoords)) {
        return prevState;
      }

      return {
        ...prevState,
        ab_line: prevState.ab_line.map((line, idx) =>
          idx === abLineIndex
            ? {
                ...line,
                geojson: {
                  ...line.geojson,
                  geometry: {
                    ...line.geojson.geometry,
                    type: "LineString",
                    coordinates: nextCoords,
                  },
                },
              }
            : line
        ),
      };
    });
  }

  if (abItem.id) {
    removeLayerAndSources(map, abItem.id);
  }

  const [marker1, marker2] = addMarkersToAbLine(map, pointA, pointB, abItem, {
    createCustomMarkerElement,
    sourceId,
    polygonGeoJSON,
    getAbLineColor,
    setNewTrialState,
    abLineIndex,
    currentStep,
    trialProtocolData,
  });

  const machinaryGridArray = getMachineryGridArray(newTrialState);

  const line = turf.lineString([pointA, pointB] as any);
  map.addSource(sourceId, { type: "geojson", data: line });
  map.addLayer({
    id: layerId,
    type: "line",
    source: sourceId,
    paint: {
      "line-color": getAbLineColor(abItem?.id as number, "hexCode"),
      "line-width": 4,
      "line-dasharray": [2, 2],
    },
  });

  updateLineSource({
    machinaryGridArray,
    marker1,
    marker2,
    map,
    abItem,
    polygonGeoJSON,
    getAbLineColor,
    trialProtocolData,
    currentStep,
  });
};

const MAX_INTERIOR_SAMPLE_ATTEMPTS = 60;

/**
 * Two random points that fall INSIDE the field polygon (not merely inside its
 * bounding box), so a freshly generated AB line always sits on the field. We
 * still sample within the same 5%-inset bounding box as before, but keep only
 * points that are actually inside the polygon: for a concave field or a
 * MultiPolygon the bbox includes gaps that are NOT on the field, and the old
 * bbox-only random could drop the whole line into such a gap. If sampling can't
 * find two interior points (e.g. a sliver polygon) we fall back to boundary
 * vertices, which are still on the field.
 */
const generateInteriorLinePoints = (
  polygon: turf.Polygon | turf.MultiPolygon
): [mapboxgl.LngLatLike, mapboxgl.LngLatLike] => {
  const [minX, minY, maxX, maxY] = turf.bbox(polygon);
  const width = maxX - minX;
  const height = maxY - minY;

  // Use small 5% margin inward to keep points close to boundary (unchanged).
  const marginX = width * 0.05;
  const marginY = height * 0.05;
  const constrainedBbox: [number, number, number, number] = [
    minX + marginX,
    minY + marginY,
    maxX - marginX,
    maxY - marginY,
  ];

  const points: number[][] = [];
  for (
    let attempt = 0;
    attempt < MAX_INTERIOR_SAMPLE_ATTEMPTS && points.length < 2;
    attempt++
  ) {
    const candidate = turf.randomPoint(1, { bbox: constrainedBbox }).features[
      INDEX_ZERO
    ].geometry.coordinates;
    if (!turf.booleanPointInPolygon(turf.point(candidate), polygon)) continue;
    const isDuplicate =
      points.length === 1 &&
      candidate[0] === points[0][0] &&
      candidate[1] === points[0][1];
    if (isDuplicate) continue;
    points.push(candidate);
  }

  if (points.length === 2) {
    return [points[0] as mapboxgl.LngLatLike, points[1] as mapboxgl.LngLatLike];
  }

  // Sliver fallback: two boundary vertices — always on the field.
  const vertices = turf
    .explode(polygon)
    .features.map((feature) => feature.geometry.coordinates);
  const a = vertices[0];
  const b = vertices[Math.floor(vertices.length / 2)] ?? vertices.at(-1) ?? a;
  return [a as mapboxgl.LngLatLike, b as mapboxgl.LngLatLike];
};

/**
 * Slide a generated line onto the field (keeping its bearing) when it landed
 * fully outside — used for the 2nd+ "crossing" AB lines, whose geometry is
 * derived from the first line and can otherwise miss a small/off-centre field.
 * Translating the whole segment so its midpoint sits on the field guarantees it
 * intersects the field without changing its direction.
 */
const shiftLineOntoField = (
  pointA: number[],
  pointB: number[],
  polygon: turf.Polygon | turf.MultiPolygon
): [mapboxgl.LngLatLike, mapboxgl.LngLatLike] => {
  try {
    // Translate the whole segment by the planar lng/lat delta that moves its
    // midpoint onto a point guaranteed to sit on the field. Same delta on both
    // endpoints ⇒ the segment's direction is unchanged; midpoint == target ⇒ the
    // line now passes through the field. A planar delta (rather than a rhumb
    // translate) keeps this exact for the small, local shifts a crossing line
    // ever needs, matching how the rest of this file treats coordinates.
    const target = turf.pointOnFeature(turf.feature(polygon)).geometry
      .coordinates;
    const midLng = (pointA[INDEX_ZERO] + pointB[INDEX_ZERO]) / 2;
    const midLat = (pointA[INDEX_ONE] + pointB[INDEX_ONE]) / 2;
    const deltaLng = target[INDEX_ZERO] - midLng;
    const deltaLat = target[INDEX_ONE] - midLat;
    return [
      [
        pointA[INDEX_ZERO] + deltaLng,
        pointA[INDEX_ONE] + deltaLat,
      ] as mapboxgl.LngLatLike,
      [
        pointB[INDEX_ZERO] + deltaLng,
        pointB[INDEX_ONE] + deltaLat,
      ] as mapboxgl.LngLatLike,
    ];
  } catch {
    return [pointA as mapboxgl.LngLatLike, pointB as mapboxgl.LngLatLike];
  }
};

const getLinePoints = (
  abItem: AbLine,
  polygon: turf.Polygon | turf.MultiPolygon
): [mapboxgl.LngLatLike, mapboxgl.LngLatLike] => {
  const coordinates = abItem.geojson.geometry.coordinates;
  const isPlaceholder =
    coordinates[INDEX_ZERO] === 0 && coordinates[INDEX_ONE] === 0;
  const hasTwoPoints =
    Array.isArray(coordinates[INDEX_ZERO]) &&
    Array.isArray(coordinates[INDEX_ONE]);

  // First AB line (still the [0,0] placeholder) or any not-yet-realized/invalid
  // geometry: generate random points INSIDE the field so the line sits on it.
  if (isPlaceholder || !hasTwoPoints) {
    return generateInteriorLinePoints(polygon);
  }

  const pointA = coordinates[INDEX_ZERO] as number[];
  const pointB = coordinates[INDEX_ONE] as number[];

  // Subsequent (crossing) AB lines keep their generated geometry as long as it
  // touches the field; only if it landed fully outside do we nudge it back on,
  // preserving its bearing. This never fires for a user-dragged line — getLinePoints
  // runs only at a line's initial creation, not on drag/edit. "Touches" = the
  // segment crosses the field boundary OR an endpoint lies inside it (a line fully
  // contained in the field has no boundary crossing but is still on it).
  let touchesField = true;
  try {
    const line = turf.lineString([pointA, pointB]);
    touchesField =
      turf.lineIntersect(line, polygon).features.length > 0 ||
      turf.booleanPointInPolygon(turf.point(pointA), polygon) ||
      turf.booleanPointInPolygon(turf.point(pointB), polygon);
  } catch {
    touchesField = true; // never discard a line just because the check threw
  }

  if (touchesField) {
    return [pointA as mapboxgl.LngLatLike, pointB as mapboxgl.LngLatLike];
  }
  return shiftLineOntoField(pointA, pointB, polygon);
};

const getMachineryGridArray = (newTrialState: any): any[] => {
  if (newTrialState.ab_line.length === 0) return [];
  return newTrialState.ab_line.map((abItem: any) => ({
    id: `grid-layer-${abItem.id as string}`,
    ABLine_id: abItem.id,
    name: "Machinery Grid",
    key: "Machinery Grid",
    grid_width: abItem.grid_width,
    visible: abItem.visible,
    rotation: 0,
  }));
};

interface UpdateLineSourceOnDragOptions {
  marker1: mapboxgl.Marker;
  marker2: mapboxgl.Marker;
  map: mapboxgl.Map;
  sourceId: string;
  abItem: AbLine;
  polygonGeoJSON: PolygonGeoJSON;
  getAbLineColor: (id: number, type: string) => string;
  setNewTrialState?: (
    updater: (prevState: NewTrialStateInterface) => NewTrialStateInterface
  ) => void;
  abLineIndex?: number;
  trialProtocolData?: any;
  currentStep?: number;
}

const updateLineSourceOnDrag = (
  options: UpdateLineSourceOnDragOptions
): void => {
  const {
    marker1,
    marker2,
    map,
    sourceId,
    abItem,
    polygonGeoJSON,
    getAbLineColor,
    setNewTrialState,
    abLineIndex,
  } = options;
  const updatedPointA = marker1.getLngLat().toArray();
  const updatedPointB = marker2.getLngLat().toArray();
  const line = turf.lineString([updatedPointA, updatedPointB]);
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource;
  source.setData(line);
  abItem.geojson.geometry.coordinates = [updatedPointA, updatedPointB] as any;

  // Update ab_line coordinates in state so input reflects drag
  if (setNewTrialState && typeof abLineIndex === "number") {
    setNewTrialState((prevState: NewTrialStateInterface) => ({
      ...prevState,
      ab_line: prevState?.ab_line.map((line, idx) =>
        idx === abLineIndex
          ? {
              ...line,
              geojson: {
                ...line.geojson,
                geometry: {
                  ...line.geojson.geometry,
                  coordinates: [updatedPointA, updatedPointB],
                },
              },
            }
          : line
      ),
    }));

    // Clear strip lines cache to force immediate redraw with new AB-line direction
    clearStripLinesCache();
    // Check whether plots are still aligned with the new AB-line direction
    checkAlignmentWithCurrentState();
  }

  const sourceIdGrid = `grid-layer-${abItem.id ?? ""}`;
  // Use turf.feature (not turf.polygon) so MultiPolygon field geometries are
  // passed through intact — the machinery grid is then clipped inside every
  // sub-polygon. turf.polygon() would throw on a MultiPolygon's coordinates.
  const turfPolygon = turf.feature(
    polygonGeoJSON.features[0].geometry
  ) as turf.Feature<turf.Polygon | turf.MultiPolygon>;

  const layerId = `grid-line-layer-${abItem.id ?? ""}`;
  const shouldDrawMiddleLines = true;
  drawMachineryGridPure(
    map,
    turfPolygon,
    updatedPointA,
    updatedPointB,
    sourceIdGrid,
    layerId,
    {
      spacing: abItem.grid_width,
      color: getAbLineColor(abItem.id as number, "hexCode"),
      drawGreyLines: shouldDrawMiddleLines,
      visible: abItem.visible ?? true,
    }
  );
};

interface UpdateLineSourceOptions {
  machinaryGridArray: any[];
  marker1: mapboxgl.Marker;
  marker2: mapboxgl.Marker;
  map: mapboxgl.Map;
  abItem: AbLine;
  polygonGeoJSON: PolygonGeoJSON;
  getAbLineColor: (id: number, type: string) => string;
  trialProtocolData?: any;
  currentStep?: number;
}

const updateLineSource = (options: UpdateLineSourceOptions): void => {
  const { marker1, marker2, map, abItem, polygonGeoJSON, getAbLineColor } =
    options;
  const updatedPointA = marker1.getLngLat().toArray();
  const updatedPointB = marker2.getLngLat().toArray();
  const line = turf.lineString([updatedPointA, updatedPointB]);
  const source = map.getSource(
    `ab-line-geojson-data-${abItem.id as number}`
  ) as mapboxgl.GeoJSONSource;
  source.setData(line);
  abItem.geojson.geometry.coordinates = [updatedPointA, updatedPointB] as any;
  // Use turf.feature (not turf.polygon) so MultiPolygon field geometries are
  // passed through intact — the machinery grid is then clipped inside every
  // sub-polygon. turf.polygon() would throw on a MultiPolygon's coordinates.
  const turfPolygon = turf.feature(
    polygonGeoJSON.features[0].geometry
  ) as turf.Feature<turf.Polygon | turf.MultiPolygon>;
  const sourceId = `grid-layer-${abItem.id ?? ""}`;
  const layerId = `grid-line-layer-${abItem.id ?? ""}`;
  const shouldDrawMiddleLines = true;
  drawMachineryGridPure(
    map,
    turfPolygon,
    updatedPointA,
    updatedPointB,
    sourceId,
    layerId,
    {
      spacing: abItem.grid_width,
      color: getAbLineColor(abItem.id as number, "hexCode"),
      drawGreyLines: shouldDrawMiddleLines,
      visible: abItem.visible ?? true,
    }
  );
};

const calculateAngleDegrees = (pointA: number[], pointB: number[]): number => {
  const angleRadians = Math.atan2(pointB[1] - pointA[1], pointB[0] - pointA[0]);
  return (angleRadians * 180) / Math.PI;
};

const createMarker = (
  map: mapboxgl.Map,
  point: mapboxgl.LngLatLike,
  icon: string,
  markerIndex: number,
  id: number,
  createCustomMarkerElement: (icon: string) => HTMLElement,
  isSelected: boolean
): mapboxgl.Marker => {
  const markerId = `marker${markerIndex + 1}-${id}`;
  const marker = new mapboxgl.Marker({
    draggable: isSelected,
    element: createCustomMarkerElement(icon),
  })
    .setLngLat(point)
    .addTo(map);

  mapboxMarkers.set(markerId, marker);
  return marker;
};

const removeLayerAndSources = (map: mapboxgl.Map, id: number): void => {
  const layerId = `ab-line-layer-${id}`;
  const sourceId = `ab-line-geojson-data-${id}`;
  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }
  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }

  const marker1Id = `marker1-${id}`;
  const marker2Id = `marker2-${id}`;

  mapboxMarkers.get(marker1Id)?.remove();
  mapboxMarkers.get(marker2Id)?.remove();

  // Remove regular grid lines
  const temp = map.getSource(`grid-layer-${id}`);
  if (temp) {
    map.removeLayer(`grid-line-layer-${id}`);
    map.removeSource(`grid-layer-${id}`);
  }

  // Remove gray intermediate grid lines
  const graySourceId = `grid-layer-${id}-gray`;
  const grayLayerId = `grid-line-layer-${id}-gray`;
  if (map.getLayer(grayLayerId)) {
    map.removeLayer(grayLayerId);
  }
  if (map.getSource(graySourceId)) {
    map.removeSource(graySourceId);
  }
};

const updateLayerVisibilityAndSelection = (
  map: mapboxgl.Map,
  layerId: string,
  visible: boolean | undefined,
  id: number,
  isSelected: boolean,
  polygonGeoJSON: any
): void => {
  map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");

  const marker1Id = `marker1-${id}`;
  const marker2Id = `marker2-${id}`;
  const marker1 = mapboxMarkers.get(marker1Id);
  if (marker1) {
    marker1.getElement().style.display = visible ? "block" : "none";
    marker1.setDraggable(isSelected);
  }
  const marker2 = mapboxMarkers.get(marker2Id);
  if (marker2) {
    marker2.getElement().style.display = visible ? "block" : "none";
    marker2.setDraggable(isSelected);
  }

  const gridId = `grid-line-layer-${id}`;
  if (map.getLayer(gridId)) {
    map.setLayoutProperty(gridId, "visibility", visible ? "visible" : "none");
  }

  const grayGridId = `grid-line-layer-${id}-gray`;
  if (map.getLayer(grayGridId)) {
    map.setLayoutProperty(
      grayGridId,
      "visibility",
      visible ? "visible" : "none"
    );
  }
};

// Exports for testing internal functions
export { getLinePoints, getMachineryGridArray, calculateAngleDegrees };
